import { Reducer } from 'redux';
import { update } from '../helpers/immutabilityHelper'
import * as Dtos from '../dtos/Tmd.dtos';

import {
    ACTION_TASKTYPE_LOAD,
    ACTION_TASKTYPE_LOAD_BY_ID,
    ACTION_TASKTYPE_LOAD_FAILURE,
    ACTION_TASKTYPE_LOAD_SUCCESS,

    ACTION_TASKTYPE_SAVE,
    ACTION_TASKTYPE_SAVE_SUCCESS,
    ACTION_TASKTYPE_SAVE_FAILURE,

    ACTION_TASKTYPE_CREATE,
    ACTION_TASKTYPE_CREATE_SUCCESS,
    ACTION_TASKTYPE_CREATE_FAILURE,

    ACTION_TASKTYPE_VALIDATE,
    ACTION_TASKTYPE_VALIDATE_SUCCESS,
    ACTION_TASKTYPE_VALIDATE_FAILURE,

    ACTION_TASKTYPE_CLEAR,
    ACTION_TASKTYPE_FILTER,

    TaskTypeAction
} from '../actions/taskTypes';
import { RequestState } from "../enumerations/RequestState";
import { IRequestState, IFormFilter } from "./index";
import { updateFailureIndexToId } from "../helpers/formErrorHelper";

export interface ITaskTypeState {
    formData: Dtos.TaskType | Dtos.TaskType[];
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    validationFailures: Dtos.ResponseError[];
    lookups: Dtos.Lookup[];
    loadState: IRequestState;
    saveState: IRequestState;
    updateState: IRequestState;
    createState: IRequestState;
    validateState: IRequestState;
    formFilter: IFormFilter;
    formFilteredData: Dtos.TaskType[];
}

const initialState: ITaskTypeState = {
    formData: undefined,
    formState: undefined,
    formProperties: undefined,
    validationFailures: undefined,
    lookups: undefined,
    loadState: {
        status: RequestState.None
    } as IRequestState,
    saveState: {
        status: RequestState.None
    } as IRequestState,
    updateState: {
        status: RequestState.None
    } as IRequestState,
    createState: {
        status: RequestState.None
    } as IRequestState,
    validateState: {
        status: RequestState.None
    } as IRequestState,
    formFilter: {
        search: "",
        showInActive: false
    },
    formFilteredData: []
};


function loading(state: ITaskTypeState, _isLoading: boolean): ITaskTypeState {
    const newState: ITaskTypeState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function loadSuccess(state: ITaskTypeState,
    formData: Dtos.TaskType | Dtos.TaskType[],
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[],
    responseStatus: Dtos.ResponseStatus
): ITaskTypeState {
    const newState: ITaskTypeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        loadState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return filter(newState, state.formFilter);
}

function filter(state: ITaskTypeState, formFilter: IFormFilter): ITaskTypeState {

    const interests: Dtos.TaskType[] | undefined =
        state.formData instanceof Array ?
            state.formData :
            undefined;

    const formFilteredData = interests ? interests.filter(p => (p.active == true || formFilter.showInActive) &&
        (formFilter.search == "" || p.value.toLowerCase().indexOf(formFilter.search.toLowerCase()) > -1)) : undefined;

    const newState: ITaskTypeState = update(state, {
        formFilter: {
            $set: formFilter
        },
        formFilteredData: {
            $set: formFilteredData
        }
    });

    return newState;
}

function loadFailure(state: ITaskTypeState, responseStatus: Dtos.ResponseStatus): ITaskTypeState {
    const newState: ITaskTypeState = update(state, {
        loadState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function clear(state: ITaskTypeState, propertiesToClear?: string[]): ITaskTypeState {

    if (!propertiesToClear) {
        let formFilter = state.formFilter;
        const newState: ITaskTypeState = update(initialState, {
            formFilter: {
                $set: formFilter
            }
        });
        return newState;
    }

    let stateModifier: any = {};

    propertiesToClear.forEach(propertyToClear => {
        stateModifier[propertyToClear] = { $set: initialState[propertyToClear] }
    });

    const newState = update(state, stateModifier);

    return newState;
}

function saving(state: ITaskTypeState, _isSaving: boolean): ITaskTypeState {
    const newState: ITaskTypeState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function saveSuccess(
    state: ITaskTypeState,
    formData: Dtos.TaskType | Dtos.TaskType[],
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITaskTypeState {
    const newState: ITaskTypeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        saveState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function saveFailure(state: ITaskTypeState, responseStatus: Dtos.ResponseStatus): ITaskTypeState {
    const newState: ITaskTypeState = update(state, {
        saveState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

function creating(state: ITaskTypeState, _isCreating: boolean): ITaskTypeState {
    const newState: ITaskTypeState = update(state, {
        createState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function createSuccess(
    state: ITaskTypeState,
    formData: Dtos.TaskType,
    formState: Dtos.FormState,
    formProperties: Dtos.FormProperty[],
    lookups: Dtos.Lookup[]
): ITaskTypeState {
    const newState: ITaskTypeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        formProperties: {
            $set: formProperties
        },
        lookups: {
            $set: lookups
        },
        createState: {
            $set: {
                status: RequestState.Success
            } as IRequestState
        }
    });

    return newState;
}

function createFailure(state: ITaskTypeState, responseStatus: Dtos.ResponseStatus): ITaskTypeState {
    const newState: ITaskTypeState = update(state, {
        createState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors
            } as IRequestState
        }
    });

    return newState;
}

function validating(state: ITaskTypeState, _isValidating: boolean): ITaskTypeState {
    const newState: ITaskTypeState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Pending
            } as IRequestState
        }
    });

    return newState;
}

function validateSuccess(
    state: ITaskTypeState,
    formData: Dtos.TaskType,
    formState: Dtos.FormState,
    responseStatus: Dtos.ResponseStatus
): ITaskTypeState {
    const newState: ITaskTypeState = update(state, {
        formData: {
            $set: formData
        },
        formState: {
            $set: formState
        },
        validateState: {
            $set: {
                status: RequestState.Success,
                errorCode: responseStatus ? responseStatus.errorCode : undefined,
                errorMessage: responseStatus ? responseStatus.message : undefined,
                errors: responseStatus ? responseStatus.errors : undefined,
                meta: responseStatus ? responseStatus.meta : undefined
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, formData) : undefined
        }
    });

    return newState;
}

function validateFailure(state: ITaskTypeState, responseStatus: Dtos.ResponseStatus): ITaskTypeState {
    const newState: ITaskTypeState = update(state, {
        validateState: {
            $set: {
                status: RequestState.Failure,
                errorCode: responseStatus?.errorCode,
                errorMessage: responseStatus?.message,
                errors: responseStatus?.errors,
                meta: responseStatus.meta
            } as IRequestState
        },
        validationFailures: {
            $set: responseStatus ? updateFailureIndexToId(responseStatus.errors, state.formData) : undefined
        }
    });

    return newState;
}

const TaskTypeReducer: Reducer<ITaskTypeState> = (state: ITaskTypeState = initialState, action: TaskTypeAction) => {
    switch (action.type) {
        case ACTION_TASKTYPE_LOAD:
        case ACTION_TASKTYPE_LOAD_BY_ID:
            return loading(state, true);
        case ACTION_TASKTYPE_LOAD_SUCCESS:
            return loadSuccess(state, action.formData, action.formState, action.formProperties, action.lookups, action.responseStatus);
        case ACTION_TASKTYPE_LOAD_FAILURE:
            return loadFailure(state, action.responseStatus);
        case ACTION_TASKTYPE_CLEAR:
            return clear(state, action.propertiesToClear);
        case ACTION_TASKTYPE_SAVE:
            return saving(state, true);
        case ACTION_TASKTYPE_SAVE_SUCCESS:
            return saveSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TASKTYPE_SAVE_FAILURE:
            return saveFailure(state, action.responseStatus);
        case ACTION_TASKTYPE_CREATE:
            return creating(state, true);
        case ACTION_TASKTYPE_CREATE_SUCCESS:
            return createSuccess(state, action.formData, action.formState, action.formProperties, action.lookups);
        case ACTION_TASKTYPE_CREATE_FAILURE:
            return createFailure(state, action.responseStatus);
        case ACTION_TASKTYPE_VALIDATE:
            return validating(state, true);
        case ACTION_TASKTYPE_VALIDATE_SUCCESS:
            return validateSuccess(state, action.formData, action.formState, action.responseStatus);
        case ACTION_TASKTYPE_VALIDATE_FAILURE:
            return validateFailure(state, action.responseStatus);
        case ACTION_TASKTYPE_FILTER:
            return filter(state, action.formFilter);

    }

    return state;
}

export default TaskTypeReducer;